/**
 * What a 3D plan pane shows while its drawing is slow, or once it has failed
 * (`spec-onboarding-3d-plan-renderer-switch`, R2.2 to R2.4 and R2.7).
 *
 * A positioning host only, exactly like `UnderlayStatusBanner` and `DropStatusBanner`: the message
 * is the design-system `Banner` and the action its `Button`. Bottom-centre, above the plan pane's
 * scroll lane, so it never covers the top-centre overlays. `raised` lifts it one banner higher, for
 * the 3D editor, where `UnderlayStatusBanner` can hold the slot at the same time.
 */
import { useLayoutEffect, useState, type MouseEvent } from 'react';
import styled from 'styled-components';
import { Banner, Button } from '@repo/design-system';
import { space, zIndex } from '@repo/design-tokens';
import { PLAN_PANE_SCROLL_LANE } from '@repo/ui/planner';
import type { PlanDrawStatusView } from './planDrawStatusView';

/**
 * The height of one `UnderlayStatusBanner` with a title and a one-line body, measured in the browser
 * (Floor Plan on PUL-CON, 1440×900): what a raised banner clears when no underlay status is showing.
 * When one is, the raised banner clears its measured height instead, so a wrapped body or a Retry
 * row never sits under this banner.
 */
export const PLAN_STATUS_BANNER_ROW_PX = 64;

/** The planner frame's PDF status (`UnderlayStatusBanner`), which the editor's banner stacks above. */
const UNDERLAY_STATUS = '[data-testid="underlay-status"]';

/**
 * The height to clear for a raised banner: the underlay status's own height while it shows, else
 * `PLAN_STATUS_BANNER_ROW_PX`. The underlay status sits in the planner frame around the 3D editor,
 * so the nearest ancestor holding one is searched, and re-measured as it comes, goes or resizes.
 */
const useRaisedLift = (host: HTMLElement | null, raised: boolean) => {
  const [lift, setLift] = useState(PLAN_STATUS_BANNER_ROW_PX);
  useLayoutEffect(() => {
    if (!raised || !host) return undefined;
    let root: HTMLElement | null = host.parentElement;
    for (let depth = 0; root && depth < 8 && !root.querySelector(UNDERLAY_STATUS); depth++) {
      root = root.parentElement;
    }
    const scope = root ?? host.parentElement;
    if (!scope) return undefined;
    let observed: Element | null = null;
    // Older test environments have no ResizeObserver; the mutation watch still re-measures there.
    const sizes =
      typeof ResizeObserver === 'undefined'
        ? { observe: () => undefined, unobserve: () => undefined, disconnect: () => undefined }
        : new ResizeObserver(() => measure());
    const measure = () => {
      const underlay = scope.querySelector(UNDERLAY_STATUS);
      if (underlay !== observed) {
        if (observed) sizes.unobserve(observed);
        if (underlay) sizes.observe(underlay);
        observed = underlay;
      }
      const height = underlay?.getBoundingClientRect().height ?? 0;
      setLift(height > 0 ? Math.ceil(height) : PLAN_STATUS_BANNER_ROW_PX);
    };
    const changes = new MutationObserver(measure);
    changes.observe(scope, { childList: true, subtree: true });
    measure();
    return () => {
      changes.disconnect();
      sizes.disconnect();
    };
  }, [host, raised]);
  return lift;
};

// Token values, not the planner's theme: not every pane that draws this is inside its provider.
const BannerHost = styled.div<{ $raised: boolean; $lift: number }>`
  position: absolute;
  bottom: calc(
    ${PLAN_PANE_SCROLL_LANE}px + ${space[3]}
      ${({ $raised, $lift }) => ($raised ? `+ ${$lift}px + ${space[2]}` : '')}
  );
  left: 50%;
  transform: translateX(-50%);
  z-index: ${zIndex.sticky};
  max-width: min(560px, calc(100% - ${space[8]}));
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;

const RetryRow = styled.div`
  margin-top: ${space[2]};
`;

export interface PlanDrawStatusBannerProps {
  /** What to say, from `planDrawStatusView`; `null` shows nothing. */
  view: PlanDrawStatusView | null;
  /** Draw the pane's map again. */
  onRetry?: () => void;
  /** Sit one banner higher than the underlay status (the 3D editor). */
  raised?: boolean;
}

/**
 * The plan pane a Retry press belongs to: the nearest focusable ancestor (the read-only frame), else
 * the pane region beside the banner (the editor). Retry unmounts the banner, button and all, so
 * without this a keyboard user's focus falls back to the page.
 */
const paneOf = (button: HTMLElement): HTMLElement | null => {
  const ancestor = button.parentElement?.closest<HTMLElement>('[tabindex="0"]');
  if (ancestor) return ancestor;
  const host = button.closest<HTMLElement>('[data-plan-draw-status-host]');
  return host?.parentElement?.querySelector<HTMLElement>('[role="region"][tabindex="0"]') ?? null;
};

export function PlanDrawStatusBanner({ view, onRetry, raised = false }: PlanDrawStatusBannerProps) {
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const lift = useRaisedLift(host, raised && view !== null);
  if (!view) return null;
  const retry = (event: MouseEvent<HTMLElement>) => {
    const pane = paneOf(event.currentTarget);
    onRetry?.();
    pane?.focus({ preventScroll: true });
  };
  return (
    <BannerHost ref={setHost} $raised={raised} $lift={lift} data-plan-draw-status-host>
      <Banner
        key={`${view.tone}:${view.title}`}
        tone={view.tone}
        surface="on-solid"
        role={view.role}
        title={view.title}
        data-testid="plan-draw-status"
      >
        {view.body}
        {view.retryLabel && onRetry && (
          <RetryRow>
            <Button variant="ghost" size="xs" aria-label={view.retryLabel} onClick={retry}>
              Retry
            </Button>
          </RetryRow>
        )}
      </Banner>
    </BannerHost>
  );
}
